import { logoutApi } from '@/api/auth/auth.ts'
import { getOrganization } from '@/api/organization/organization.ts'
import type { Organization } from '@/api/organization/types.ts'
import type { CurrentUser } from '@/api/users/types.ts'
import { Roles } from '@/api/users/types.ts'
import { authMe } from '@/api/users/users.ts'
import { TOKEN_KEY } from '@/constants/auth.ts'
import router from '@/router'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const USER_DATA_KEY = 'user_data'

const getStoredUserData = (): CurrentUser | null => {
  try {
    const raw = localStorage.getItem(USER_DATA_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const userStore = defineStore('userStore', () => {
  const userData = ref<CurrentUser | null>(getStoredUserData())
  const organizationData = ref<Organization | null>(null)

  const getUserData = computed(() => userData.value)
  const getOrganizationData = computed(() => organizationData.value)

  const isCustomer = computed(() => userData.value?.role === Roles.CUSTOMER)
  const isAdmin = computed(() => userData.value?.role === Roles.ADMIN)
  const isSuperAdmin = computed(() => userData.value?.role === Roles.SUPER_ADMIN)
  const isSuperUser = computed(() => userData.value?.role === Roles.SUPER_USER)
  const isAnyAdmin = computed(() => isAdmin.value || isSuperAdmin.value || isSuperUser.value)

  const getCurrentUser = async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      return
    }

    try {
      const response = await authMe()
      userData.value = response.data
      try {
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(response.data))
      } catch (storageError) {
        console.error('Failed to store user data:', storageError)
      }

      if (userData?.value?.organizationId) {
        try {
          const orgResponse = await getOrganization(userData.value?.organizationId)
          organizationData.value = orgResponse.data
        } catch (orgError) {
          console.log('Error fetching organization:', orgError)
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if ([401, 404].includes(error.response?.status || 0)) {
          console.log('logout')
          logout()
        } else {
          console.log('error', error)
        }
      }
    }
  }

  const logout = () => {
    try {
      logoutApi()
    } catch (e) {
      // ignore
    }
    userData.value = null
    organizationData.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_DATA_KEY)
    if (router.currentRoute.value?.meta?.requiresAuth) {
      router.push('/auth')
    }
  }

  return {
    getUserData,
    getOrganizationData,
    isCustomer,
    isAdmin,
    isSuperAdmin,
    isSuperUser,
    isAnyAdmin,
    getCurrentUser,
    logout,
  }
})
